import React, { useState } from "react";
import { useCookies } from "react-cookie";

import Modal from "../Modal";
import Button from "../Button";
import ModalErrorMessage from "../ModalErrorMessage";
import LoadingSpinner from "../Spinner";
import { InviteUsersModalProps } from "./types";
import {
  ModalTitle,
  InputRow,
  Input,
  ActionsRow,
  ModalWrapper,
} from "./styles";

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  open,
  onCancel,
  onInvite,
  agencyId,
}) => {
  const [emails, setEmails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cookies] = useCookies(['user-id']);

  const validateEmails = (emailsStr: string) => {
    const emailArray = emailsStr
      .split(/[\s,]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emailArray.length === 0) {
      return { valid: false, invalidEmails: [], emailArray, error: "Please enter at least one email." };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailArray.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      return {
        valid: false,
        invalidEmails,
        emailArray,
        error: `Invalid emails: ${invalidEmails.join(", ")}`
      };
    }
    return { valid: true, invalidEmails: [], emailArray, error: null };
  };

  const handleInvite = async () => {
    const emailValidation = validateEmails(emails);
    if (!emailValidation.valid) {
      setError(emailValidation.error);
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const resp = await (await import("../../api/admin/adminApi")).default.adminInviteUsers(agencyId, emailValidation.emailArray);
      setLoading(false);
      if (resp.status !== 200) {
        setError(resp.body?.message || "Failed to send invitations.");
      } else {
        onInvite?.(emailValidation.emailArray.join(", "));
        setEmails("");
      }
    } catch (e) {
      setLoading(false);
      setError("Failed to send invitations.");
    }
  };

  if (!open) return null;

  return (
    <Modal onClick={onCancel}>
      <ModalWrapper>
        <ModalTitle>Invite people</ModalTitle>
        {error && <ModalErrorMessage message={error} inline />}
        {loading ? (
          <div style={{ width: "100%", textAlign: "center", marginBottom: "70px" }}>
            <div style={{ marginBottom: 16, fontSize: 18, fontWeight: 500 }}>Sending emails...</div>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div style={{ fontSize: 20 }}>
              Enter emails to invite:
            </div>
            <InputRow>
              <Input
                type="text"
                placeholder="email1@example.com, email2@example.com..."
                value={emails}
                onChange={e => setEmails(e.target.value)}
              />
            </InputRow>
            <ActionsRow>
              <Button 
                text="Cancel" 
                handleClick={onCancel} 
                $height="50px"
              />
              <Button 
                text="Invite" 
                handleClick={handleInvite} 
                $height="50px"
                $secondary
              />
            </ActionsRow>
          </>
        )}
      </ModalWrapper>
    </Modal>
  );
};

export default InviteUsersModal;
